"use client";

import { useActionState } from "react";

import Image from "next/image";

import { Link } from "@/components/link/link.component";
import { SalesPrice } from "@/components/salesPrice/salesPrice.component";
import { Text } from "@/components/text/text.component";

import {
  buildEcommerceEvent,
  createEcommercePayload,
  mapProductToItem,
} from "@/lib/analytics/ecommerce-analytics";
import { LineItemOptions } from "@/lib/basket/basket.model";
import { ProductLineItemModel } from "@/lib/basket/productLineItem.model";

import { removeKitFromCart } from "@/app/blu/(checkout-flow)/actions";
import { EVENTS } from "@/constants/analytics.constants";
import { productPage, SHOP_PAGE } from "@/constants/page.constants";
import { useTagging } from "@/hooks/useTagging";
import { GetCartLineProductsQueryResult } from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

import { CartKitLineItemOptions } from "./cartKitLineItemOptions.component";

const initialCartItemState: ResponseMessage = { success: false };

export const CartKitLineItem = ({
  kit,
  product,
  hideControls,
  options,
  showQuantity = false,
}: CartKitLineItemProps) => {
  const [, setTag] = useTagging();
  const [, removeKitAction] = useActionState<ResponseMessage, FormData>(
    removeKitFromCart,
    initialCartItemState,
  );

  if (!kit) return null;

  const fireRemoveFromCart = () => {
    const items = [
      mapProductToItem(
        {
          id: kit.productId,
          name: kit.productName,
          salesPrice: kit.salesPrice,
          listPrice: kit.listPrice,
        },
        { index: 0, add_on: "n" },
      ),
    ];
    const payload = createEcommercePayload(items, {
      value: kit.salesPrice,
    });
    setTag(buildEcommerceEvent(EVENTS.REMOVE_FROM_CART, payload), {
      reset: true,
    });
  };

  const renderControls = () => {
    if (hideControls) {
      return (
        <div className="flex justify-between md:hidden">
          <SalesPrice
            salesPrice={kit.salesPrice}
            listPrice={kit.listPrice}
            free={kit.salesPrice === 0}
            salesPriceClassName="subtitle-2"
            listPriceClassName="subtitle-2 line-through"
            reverse
          />

          {showQuantity ? (
            <Text variant="body6">Qty: {kit.quantity}</Text>
          ) : null}
        </div>
      );
    }
    return (
      <div className="mt-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <form action={removeKitAction} className="inline-flex">
            <input type="hidden" name="action" value="decrease" />
            <input type="hidden" name="itemId" value={kit.UUID} />
            <button
              type="submit"
              onClick={fireRemoveFromCart}
              className="cursor-pointer bg-transparent p-0 underline underline-offset-3 hover:bg-transparent">
              Remove
            </button>
          </form>
          <span className="h-4 w-px bg-neutral-300" aria-hidden />
          <Link
            href={
              (kit.productId ? productPage(kit.productId) : null) ?? SHOP_PAGE
            }
            className="underline underline-offset-3 hover:bg-transparent">
            Edit
          </Link>
        </div>
        <div className="md:hidden">
          <SalesPrice
            salesPrice={kit.salesPrice}
            listPrice={kit.listPrice}
            free={kit.salesPrice === 0}
            salesPriceClassName="subtitle-2"
            listPriceClassName="subtitle-2 line-through"
            reverse
          />
        </div>
      </div>
    );
  };

  const image = product?.featuredImage?.image
    ? urlFor(product?.featuredImage?.image).width(120).height(120).url()
    : null;

  const renderBundledItems = (
    bundleItems?: { quantity?: number; productName?: string }[],
  ) => {
    if (!bundleItems?.length) return null;
    return bundleItems.map((bp, i: number) => (
      <li
        data-testid="cart-kit-line-item-bundled-product"
        key={i}
        className="flex items-center gap-2">
        <Text key={i} variant="body4" className="text-greyscale-body">
          {bp.quantity ?? 1} {bp.productName ?? "Item"}
        </Text>
      </li>
    ));
  };

  return (
    <div
      data-testid="cart-kit-line-item"
      data-product-id={kit.productId}
      className="flex gap-4 border-b border-b-[#80808033] py-6">
      <div className="shrink-0">
        {image ? (
          <Image
            src={image}
            alt={product?.featuredImage?.alt ?? ""}
            width={80}
            height={80}
            className="h-16 w-16 rounded-lg object-cover md:h-20 md:w-20"
          />
        ) : null}
      </div>
      <div className="flex flex-1 flex-col gap-2">
        <Text variant="body2">{product?.name || kit.productName}</Text>
        <ul
          data-testid="cart-kit-line-item-bundled-products"
          className="flex flex-col gap-0.5">
          {renderBundledItems(
            product?.bundledProducts || kit.optionProductLineItems,
          )}
          <CartKitLineItemOptions product={product} options={options} />
        </ul>
        {renderControls()}
      </div>
      <div className="hidden shrink-0 flex-col place-content-between items-end pt-0.5 md:flex">
        <SalesPrice
          salesPrice={kit.salesPrice}
          listPrice={kit.listPrice}
          free={kit.salesPrice === 0}
          salesPriceClassName="subtitle-2"
          listPriceClassName="subtitle-2 line-through"
          reverse
        />

        {showQuantity ? <Text variant="body6">Qty: {kit.quantity}</Text> : null}
      </div>
    </div>
  );
};

type CartKitLineItemProps = {
  kit?: ProductLineItemModel | null;
  product?: GetCartLineProductsQueryResult[number];
  hideControls?: boolean;
  options: LineItemOptions;
  showQuantity?: boolean;
};
